import styled from "styled-components";

import { fontSize, fontWeight, theme } from "@tm/utility/global";
import { deviceType } from "../../../../utils";

export const FormCard = styled.div`
  background: ${theme.whiteText};
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow: hidden;

  @media ${deviceType.mobile} {
    padding: 16px;
    box-shadow: none;
  }
`;

export const FormHeader = styled.h2`
  font-weight: ${fontWeight.black};
  font-size: ${fontSize.font22};
  color: ${theme.blackText};
  margin: 0 0 24px;

  @media ${deviceType.mobile} {
    font-size: ${fontSize.font18};
    margin: 0 0 20px;
  }
`;

export const FormBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  min-width: 0;
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  column-gap: 40px;
  width: 100%;
  min-width: 0;

  @media ${deviceType.mobile} {
    grid-template-columns: minmax(0, 1fr);
    row-gap: 20px;
  }
`;

export const Separator = styled.div`
  height: 1px;
  background: ${theme.disabledGrey2};
  width: 100%;
`;

export const FieldRow = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  width: 100%;
  min-width: 0;
  box-sizing: border-box;

  @media ${deviceType.mobile} {
    gap: 8px;
  }
`;

export const LabelBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  flex: 1;
  min-width: 0;
`;

export const LabelTitle = styled.label`
  font-weight: ${fontWeight.black};
  font-size: ${fontSize.font14};
  color: ${theme.blackText};
`;

export const SubLabel = styled.span`
  font-weight: ${fontWeight.regular};
  font-size: ${fontSize.font12};
  color: ${theme.greyText6};
`;

export const Required = styled.span`
  color: ${theme.redText2};
  margin-left: 2px;
`;

export const Control = styled.div`
  flex: 1;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;

  @media ${deviceType.mobile} {
    flex: 0 1 154px;
    width: 154px;
    max-width: 46%;
  }
`;

export const InputWrap = styled.div<{
  $hasError?: boolean;
  $disabled?: boolean;
}>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${(p) => (p.$disabled ? theme.disabledGrey2 : theme.smokeWhite)};
  border: 1px solid ${(p) => (p.$hasError ? theme.redText2 : theme.lightSilver)};
  border-radius: 8px;
  height: 48px;
  padding: 0 12px;
  box-sizing: border-box;
  ${(p) => (p.$disabled ? "cursor: not-allowed;" : "")}
`;

export const Prefix = styled.span`
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.font12};
  color: ${theme.lightBlack};
`;

export const Input = styled.input`
  flex: 1;
  min-width: 0;
  border: none;
  background: transparent;
  outline: none;
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.font14};
  color: ${theme.blackText};

  &::placeholder {
    font-weight: ${fontWeight.regular};
    color: ${theme.greyText6};
  }

  &:disabled {
    color: ${theme.lightBlack};
    cursor: not-allowed;
  }
`;

export const TextAreaField = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const TextArea = styled.textarea`
  width: 100%;
  box-sizing: border-box;
  min-height: 80px;
  background: ${theme.gray97};
  border: 1px solid ${theme.gainsBoro};
  border-radius: 8px;
  outline: none;
  padding: 12px;
  font-weight: ${fontWeight.regular};
  font-size: ${fontSize.font14};
  color: ${theme.blackText};
  resize: vertical;

  &::placeholder {
    font-size: ${fontSize.font12};
    color: ${theme.greyText6};
  }

  &:disabled {
    color: ${theme.lightBlack};
    cursor: not-allowed;
    background: ${theme.disabledGrey2};
  }
`;

export const UploadAction = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
  flex-shrink: 0;

  @media ${deviceType.mobile} {
    flex: 0 1 154px;
    max-width: 46%;
  }
`;

export const UploadTrigger = styled.button`
  border: none;
  background: transparent;
  color: ${theme.blueText};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.font16};
  cursor: pointer;
  padding: 0;
  white-space: nowrap;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${deviceType.mobile} {
    white-space: normal;
    text-align: right;
    font-size: ${fontSize.font14};
    line-height: 1.3;
  }
`;

export const HiddenFileInput = styled.input`
  display: none;
`;

export const FileList = styled.ul`
  list-style: none;
  margin: 12px 0 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

export const FileChip = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 0;

  > img {
    width: 18px;
    height: 18px;
    flex-shrink: 0;
    display: block;
  }
`;

export const FileName = styled.span`
  flex: 1;
  min-width: 0;
  font-size: ${fontSize.font14};
  font-weight: ${fontWeight.regular};
  color: ${theme.greyText6};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export const FileRemove = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.span`
  display: block;
  margin-top: 4px;
  font-size: ${fontSize.font12};
  color: ${theme.redText2};
`;

export const FooterNote = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${fontSize.font12};
  color: ${theme.greyText6};
  margin-top: 4px;

  @media ${deviceType.mobile} {
    margin: 0;
    width: 100%;
    background: ${theme.yellowText2};
    border-radius: 8px;
    padding: 8px 12px;
    color: ${theme.greyText};
    box-sizing: border-box;
  }
`;

export const StickyFooter = styled.div`
  @media ${deviceType.mobile} {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 20;
    display: flex;
    flex-direction: column;
    gap: 8px;
    align-items: center;
    padding: 8px 16px 16px;
    background: ${theme.whiteText};
    box-shadow: 0 1px 7px rgba(0, 0, 0, 0.2);
  }
`;

export const FooterActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 24px;

  @media ${deviceType.mobile} {
    margin-top: 0;
    width: 100%;
    max-width: 430px;
  }
`;

export const PreviousButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: none;
  background: transparent;
  color: ${theme.blueText};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.font14};
  cursor: pointer;
  padding: 8px 0;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  @media ${deviceType.mobile} {
    display: none;
  }
`;

export const PreviousIcon = styled.span`
  display: inline-flex;
  transform: rotate(-90deg);
`;

export const SubmitButton = styled.button`
  border: none;
  border-radius: 8px;
  background: ${theme.blueText};
  color: ${theme.whiteText};
  font-weight: ${fontWeight.bold};
  font-size: ${fontSize.font14};
  padding: 12px 32px;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  @media ${deviceType.mobile} {
    width: 100%;
    height: 44px;
    padding: 12px 24px;
    font-size: ${fontSize.font16};
    font-weight: ${fontWeight.black};
    background: ${theme.blueGradient};
    text-transform: uppercase;
  }
`;
